import { TagItem, Product } from "@/types";

/**
 * Applies the P0 / P1 paywall rule: reveals 1-2 cheapest items (e.g. Paint / Cushion),
 * while hero furniture (sofas, beds, tables) remain locked with titles/links withheld until email unlock.
 */
export function applyPaywallToItems(
  items: (TagItem & { product?: Product; alternatives?: Product[] })[],
  isUnlocked: boolean
): (TagItem & { product?: Product; alternatives?: Product[] })[] {
  if (isUnlocked) {
    return items.map((item) => ({ ...item, locked: false }));
  }

  // Sort by price to identify the 2 cheapest items to reveal for free
  const sortedIndices = items
    .map((item, index) => ({ index, price: item.price_gbp * (item.quantity ?? 1) }))
    .sort((a, b) => a.price - b.price);

  const freeIndices = new Set(sortedIndices.slice(0, 2).map((s) => s.index));

  return items.map((item, index) => {
    const isFree = freeIndices.has(index);
    if (isFree) {
      return { ...item, locked: false };
    }

    return {
      ...item,
      locked: true,
      product_title: undefined, // withheld until unlock
      affiliate_url: undefined, // withheld until unlock
    };
  });
}

/**
 * Swaps a design item with an alternative product from the catalog and updates tag coordinates & price.
 */
export function swapDesignItem(
  items: (TagItem & { product?: Product; alternatives?: Product[] })[],
  targetItemId: string,
  replacementProduct: Product
): (TagItem & { product?: Product; alternatives?: Product[] })[] {
  return items.map((item) => {
    if (item.id !== targetItemId) return item;

    return {
      ...item,
      id: `tag-${replacementProduct.id}`,
      price_gbp: replacementProduct.price_gbp,
      product_title: replacementProduct.title,
      brand: replacementProduct.brand,
      image_url: replacementProduct.image_url,
      affiliate_url: `/api/out/${replacementProduct.id}`,
      in_stock: replacementProduct.in_stock,
      product: replacementProduct,
    };
  });
}

/**
 * Recalculates total look cost in GBP from current item list.
 */
export function calculateTotalLookPrice(items: TagItem[]): number {
  return Math.round(items.reduce((acc, item) => acc + item.price_gbp * (item.quantity ?? 1), 0) * 100) / 100;
}
